// Cluster ZUWEISEN (B2) — Fluent-freie Kontaktsuche im REFINED/kvp-rd-Look.
//
// Warum eine eigene Komponente und nicht `ContactSelector`: der ist durchgängig
// auf Fluent-Formularkomponenten gebaut (Input/Button/Spinner/Text), sucht erst
// auf Klick bzw. Enter und zeigt in der Trefferzeile weder Typ noch Firma. B2
// verlangt genau das Gegenteil.
//
// Warum nicht `redesign/RelationContactPicker`: dessen `.rcp-*`-Regeln in
// `contactRedesign.css` sind auf `.kvp-rd-scrim` gescopet — den gibt es in einem
// WindowManager-Fenster nicht, die Komponente wäre dort ungestylt. Außerdem
// bringt sie einen eigenen Anlage-Modus mit, den hier Weg 2 ersetzt.
//
// `makeStyles` ist Fluents Styling-Engine, KEINE Formularkomponente — es ist der
// Hausstandard aller `forms/`-Bausteine und deshalb ausdrücklich erlaubt.

import * as React from "react";
import { makeStyles, mergeClasses, shorthands } from "@fluentui/react-components";
import { REFINED } from "../../risikoanalyseStep/tokens";
import { useFormPrimitiveStyles } from "../formPrimitives";
import {
    KONTAKT_SUCHE_MIN_ZEICHEN,
    sucheKontakteFuerZuweisung,
    type KontaktTreffer,
} from "../../../services/kontaktSucheService";
import { VerzoegerteHoverKarte } from "./VerzoegerteHoverKarte";
import { mitAbbruch, SUCHE_ZEITLIMIT_MS } from "../../../hooks/useEntprellteSuche";
import {
    artText,
    kisGruppe,
    kisKuerzel,
    kisLabel,
    trefferFirmen,
    trefferIcon,
    trefferZusatz,
    weitereFirmen,
} from "./kontaktTrefferAnzeige";

export type { KontaktTreffer };

/** Debounce der Tippsuche in ms — dieselbe Größenordnung wie ContactRefField. */
const DEBOUNCE_MS = 300;
/**
 * Trefferzahl je Seite. Angefragt wird immer EINE Zeile mehr (`SEITE + 1`) —
 * kommt sie zurück, gibt es Nachschub und die Liste zeigt „Mehr laden".
 * Ohne diesen Kniff bräuchte es entweder eine zweite Abfrage oder einen
 * Gesamtzähler im Antwortformat; der Server deckelt `limit` bei 25, `SEITE + 1`
 * bleibt also im erlaubten Rahmen.
 */
const SEITE = 12;

/**
 * Ab dieser Breite (px) der Liste bzw. des Chips steht die K/I/S-Pill
 * ausgeschrieben („Kunde"), darunter nur der Buchstabe (T-26, Rückfrage 1).
 */
const KIS_AUSGESCHRIEBEN_AB_PX = 640;

/**
 * Breite eines Elements beobachten. Ohne `ResizeObserver` (jsdom, sehr alte
 * Browser) bleibt sie 0 — die Pill zeigt dann den Buchstaben, was immer passt.
 */
function useBreite(el: HTMLElement | null): number {
    const [breite, setBreite] = React.useState(0);
    React.useEffect(() => {
        if (!el || typeof ResizeObserver === "undefined") return;
        setBreite(el.getBoundingClientRect().width);
        const ro = new ResizeObserver((eintraege) => {
            const w = eintraege[0]?.contentRect.width;
            if (typeof w === "number") setBreite(w);
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, [el]);
    return breite;
}

const useStyles = makeStyles({
    erneutLink: {
        ...shorthands.border("0"),
        backgroundColor: "transparent",
        ...shorthands.padding("0"),
        font: "inherit",
        color: "inherit",
        textDecorationLine: "underline",
        cursor: "pointer",
    },
    box: { display: "grid", rowGap: "7px", position: "relative" },
    liste: {
        ...shorthands.border("1px", "solid", REFINED.line),
        ...shorthands.borderRadius("10px"),
        backgroundColor: REFINED.white,
        boxShadow: "0 6px 18px rgba(58,12,49,0.10)",
        maxHeight: "260px",
        overflowY: "auto",
        ...shorthands.margin("0"),
        ...shorthands.padding("4px"),
        listStyleType: "none",
    },
    zeile: {
        display: "flex",
        alignItems: "center",
        columnGap: "8px",
        width: "100%",
        textAlign: "left",
        ...shorthands.borderWidth("0"),
        ...shorthands.borderRadius("8px"),
        ...shorthands.padding("8px", "10px"),
        backgroundColor: "transparent",
        cursor: "pointer",
        fontFamily: REFINED.fontUI,
        fontSize: "0.9rem",
        color: REFINED.ink,
        ":hover": { backgroundColor: REFINED.aub50 },
    },
    zeileAktiv: { backgroundColor: REFINED.aub50 },
    // T-26: Der Name hat Vorrang vor dem Zusatz. Steht ein Zusatz daneben,
    // schrumpft der Name nicht und darf bis 65 % der Zeile belegen; der Zusatz
    // ist dann das einzige schrumpfende Element und wird gekürzt. Ohne Zusatz
    // darf der Name die ganze Zeile nutzen. Den vollen Text zeigt in jedem
    // Fall die Hover-Karte.
    name: {
        fontWeight: 600,
        whiteSpace: "nowrap",
        overflowX: "hidden",
        textOverflow: "ellipsis",
        flexShrink: 1,
        minWidth: 0,
    },
    nameMitZusatz: { flexShrink: 0, maxWidth: "65%" },
    icon: { display: "inline-flex", flexShrink: 0, color: REFINED.aub },
    bei: {
        color: REFINED.ink2,
        fontSize: "0.8rem",
        flexGrow: 0,
        flexShrink: 1,
        minWidth: 0,
        whiteSpace: "nowrap",
        overflowX: "hidden",
        textOverflow: "ellipsis",
    },
    plus: { color: REFINED.ink2, fontSize: "0.75rem", fontWeight: 700, flexShrink: 0 },
    kis: {
        marginLeft: "auto",
        flexShrink: 0,
        minWidth: "1.5em",
        textAlign: "center",
        ...shorthands.borderRadius("999px"),
        ...shorthands.padding("1px", "7px"),
        ...shorthands.border("1px", "solid", REFINED.line),
        color: REFINED.ink2,
        fontSize: "0.7rem",
        fontWeight: 700,
    },
    kisKunde: { backgroundColor: REFINED.okBg, color: REFINED.ok, ...shorthands.borderColor(REFINED.okBg) },
    kisInteressent: {
        backgroundColor: REFINED.goldBg,
        color: REFINED.goldInk,
        ...shorthands.borderColor(REFINED.goldLine),
    },
    // Hover-Karte (Inhalt; Rahmen/Position liefert VerzoegerteHoverKarte)
    kKopf: { display: "flex", alignItems: "flex-start", columnGap: "8px" },
    kName: { fontWeight: 700, overflowWrap: "anywhere" },
    kTyp: { color: REFINED.ink2, fontSize: "0.78rem" },
    kZeile: { marginTop: "6px", overflowWrap: "anywhere" },
    kLabel: {
        display: "block",
        color: REFINED.ink3,
        fontSize: "0.7rem",
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
    },
    kListe: { ...shorthands.margin("2px", "0", "0"), ...shorthands.padding("0", "0", "0", "16px") },
    kVerbindung: { color: REFINED.ink2 },
    mehr: {
        display: "block",
        width: "100%",
        textAlign: "center",
        ...shorthands.borderWidth("0"),
        ...shorthands.borderTop("1px", "solid", REFINED.line),
        ...shorthands.padding("9px", "10px"),
        backgroundColor: "transparent",
        fontFamily: REFINED.fontUI,
        fontSize: "0.82rem",
        fontWeight: 600,
        color: REFINED.aub,
        cursor: "pointer",
        ":hover": { backgroundColor: REFINED.aub50 },
        ":disabled": { color: REFINED.ink3, cursor: "default", backgroundColor: "transparent" },
    },
    chip: {
        display: "flex",
        alignItems: "center",
        columnGap: "8px",
        minWidth: 0,
        ...shorthands.border("1px", "solid", REFINED.line),
        ...shorthands.borderRadius("10px"),
        ...shorthands.padding("9px", "12px"),
        backgroundColor: REFINED.aub50,
        fontFamily: REFINED.fontUI,
        fontSize: "0.92rem",
        color: REFINED.ink,
    },
    aendern: {
        flexShrink: 0,
        ...shorthands.borderWidth("0"),
        backgroundColor: "transparent",
        color: REFINED.aub,
        fontFamily: REFINED.fontUI,
        fontWeight: 600,
        fontSize: "0.82rem",
        cursor: "pointer",
        textDecorationLine: "underline",
    },
});

/** K/I/S-Pill — Buchstabe oder ausgeschrieben, Langtext als aria-label/Titel. */
function KisPill(props: { t: KontaktTreffer; ausgeschrieben: boolean }): JSX.Element {
    const s = useStyles();
    const g = kisGruppe(props.t);
    return (
        <span
            className={mergeClasses(
                s.kis,
                g === "kunde" && s.kisKunde,
                g === "interessent" && s.kisInteressent,
            )}
            aria-label={kisLabel(g)}
            data-kis={kisKuerzel(g)}
        >
            {props.ausgeschrieben ? kisLabel(g) : kisKuerzel(g)}
        </span>
    );
}

/**
 * Inhalt einer Trefferzeile bzw. des Chips (T-26): Icon · Name · Zusatz
 * („bei <Firma>" bzw. Anschrift) · „+n" · K/I/S. Kundennummer, Typ-Badge und
 * Langtext-Status stehen bewusst nur noch in der Hover-Karte.
 */
function TrefferInhalt(props: { t: KontaktTreffer; kisAusgeschrieben: boolean }): JSX.Element {
    const s = useStyles();
    const { t } = props;
    const zusatz = trefferZusatz(t);
    const mehr = weitereFirmen(t);
    return (
        <>
            <span className={s.icon} aria-hidden="true" data-icon={t.art === "firma" || t.contactType === "Firma" ? "firma" : "person"}>
                {trefferIcon(t)}
            </span>
            <span className={mergeClasses(s.name, zusatz ? s.nameMitZusatz : undefined)}>{t.displayName}</span>
            {zusatz ? <span className={s.bei}>{zusatz}</span> : null}
            {mehr > 0 ? (
                <span className={s.plus} aria-label={`und ${mehr} weitere Firma${mehr === 1 ? "" : "en"}`}>
                    +{mehr}
                </span>
            ) : null}
            <KisPill t={t} ausgeschrieben={props.kisAusgeschrieben} />
        </>
    );
}

/** Status im Langtext, dahinter die K/I/S-Gruppe — nur wenn sie etwas hinzufügt. */
function statusZeile(t: KontaktTreffer): string {
    const lang = t.statusName?.trim() || "kein Status";
    const gruppe = kisLabel(kisGruppe(t));
    return lang === gruppe ? lang : `${lang} (${gruppe})`;
}

/** Alle Angaben eines Treffers — Inhalt der Hover-Karte (T-26). */
function TrefferDetails(props: { t: KontaktTreffer }): JSX.Element {
    const s = useStyles();
    const { t } = props;
    const firmen = trefferFirmen(t);
    return (
        <>
            <div className={s.kKopf}>
                <span className={s.icon} aria-hidden="true">
                    {trefferIcon(t)}
                </span>
                <div>
                    <div className={s.kName}>{t.displayName}</div>
                    <div className={s.kTyp}>
                        {artText(t)}
                        {t.contactNbrInt ? ` · ${t.contactNbrInt}` : ""}
                    </div>
                </div>
            </div>
            <div className={s.kZeile}>
                <span className={s.kLabel}>Status</span>
                {statusZeile(t)}
            </div>
            {firmen.length > 0 ? (
                <div className={s.kZeile}>
                    <span className={s.kLabel}>{firmen.length === 1 ? "Firma" : `Firmen (${firmen.length})`}</span>
                    <ul className={s.kListe}>
                        {firmen.map((fi) => {
                            const rolle = [fi.verbindung, fi.funktion].filter(Boolean).join(", ");
                            return (
                                <li key={fi.id}>
                                    {fi.name}
                                    {rolle ? <span className={s.kVerbindung}> — {rolle}</span> : null}
                                </li>
                            );
                        })}
                    </ul>
                </div>
            ) : null}
            {t.anschrift ? (
                <div className={s.kZeile}>
                    <span className={s.kLabel}>Anschrift</span>
                    {t.anschrift}
                </div>
            ) : null}
            {t.defaultEmail ? (
                <div className={s.kZeile}>
                    <span className={s.kLabel}>E-Mail</span>
                    {t.defaultEmail}
                </div>
            ) : null}
        </>
    );
}

export function KontaktSuchfeld(props: {
    label: string;
    name: string;
    placeholder?: string;
    value: KontaktTreffer | null;
    onChange: (t: KontaktTreffer | null) => void;
    /** Trefferliste auf Firmen bzw. Personen einschränken. */
    nurTyp?: "firma" | "person";
    required?: boolean;
    autoFocus?: boolean;
    hint?: string;
    /**
     * Eigene Courté-Kontakte ausblenden (PO 21.09.2026). Die Termin-Zuweisung
     * setzt das — Begründung in `sucheKontakteFuerZuweisung`.
     */
    ohneInterne?: boolean;
    /**
     * Kontakte, die nicht mehr in der Trefferliste erscheinen sollen — im
     * Mehrfach-Picker die bereits gewählten. Gefiltert wird erst NACH der
     * Antwort: eine Ausschlussliste an den Server zu geben hieße, die Suche
     * bei jeder Auswahl neu zu paginieren.
     */
    ausblendenIds?: number[];
}): JSX.Element {
    const f = useFormPrimitiveStyles();
    const s = useStyles();
    const [term, setTerm] = React.useState("");
    const [treffer, setTreffer] = React.useState<KontaktTreffer[]>([]);
    const [laedt, setLaedt] = React.useState(false);
    const [fehler, setFehler] = React.useState(false);
    const [zuLange, setZuLange] = React.useState(false);
    const [aktiv, setAktiv] = React.useState(0);
    const [beruehrt, setBeruehrt] = React.useState(false);
    const [weitere, setWeitere] = React.useState(false);
    const [laedtMehr, setLaedtMehr] = React.useState(false);
    const feldRef = React.useRef<HTMLInputElement | null>(null);
    // T-26: Hover-Karte. Die Zeilen-Elemente je Index, damit die Karte an der
    // aktiven Zeile ansetzt; „Maus in der Liste" bzw. „mit Pfeiltasten
    // unterwegs" entscheidet, ob sie (nach 2 s) erscheint.
    const zeilenRefs = React.useRef<Array<HTMLButtonElement | null>>([]);
    const [mausInListe, setMausInListe] = React.useState(false);
    const [perTastatur, setPerTastatur] = React.useState(false);
    const [listeEl, setListeEl] = React.useState<HTMLUListElement | null>(null);
    const [chipEl, setChipEl] = React.useState<HTMLDivElement | null>(null);
    const [chipHover, setChipHover] = React.useState(false);
    const listenBreite = useBreite(listeEl);
    const chipBreite = useBreite(chipEl);
    /** Läuft gerade ein Nachladen, gehört sein Controller hierher — eine neue
     *  Eingabe muss es abbrechen, sonst hängt die Antwort Treffer des ALTEN
     *  Suchbegriffs an die neue Liste. */
    const mehrRef = React.useRef<AbortController | null>(null);

    const gewaehlt = props.value;

    /**
     * Bereits gewählte Kontakte aus einer Trefferliste nehmen (Mehrfach-Picker).
     * Gefiltert wird beim SETZEN der Liste, nicht im Render: so bleiben der
     * Tastatur-Index (`aktiv`), „Mehr laden" und die Leer-Meldung auf genau der
     * Liste, die auch angezeigt wird. Eine veraltete Liste kann nicht
     * stehenbleiben, weil `waehle` sie ohnehin leert.
     */
    const ausblenden = props.ausblendenIds;
    const ohneGewaehlte = React.useCallback(
        (rows: KontaktTreffer[]): KontaktTreffer[] =>
            ausblenden?.length ? rows.filter((t) => !ausblenden.includes(t.contactId)) : rows,
        [ausblenden],
    );

    // Zähler für „erneut versuchen" — eine Erhöhung startet dieselbe Suche neu.
    const [versuch, setVersuch] = React.useState(0);

    React.useEffect(() => {
        if (gewaehlt) {
            // Der Abbruch der laufenden Suche (Cleanup) setzt `laedt` bewusst
            // nicht zurück — das muss hier geschehen, sonst steht nach „ändern"
            // kurz ein veraltetes „Suche …".
            setLaedt(false);
            return;
        }
        const s2 = term.trim();
        // Jede neue Eingabe verwirft ein laufendes Nachladen.
        mehrRef.current?.abort();
        setLaedtMehr(false);
        if (s2.length < KONTAKT_SUCHE_MIN_ZEICHEN) {
            setTreffer([]);
            setWeitere(false);
            setLaedt(false);
            setFehler(false);
            return;
        }
        const controller = new AbortController();
        setLaedt(true);
        setFehler(false);
        let zeitlimit: ReturnType<typeof setTimeout> | undefined;
        let abgeloest = false;
        let zeitUeberschritten = false;
        const handle = setTimeout(async () => {
            // Ohne Antwort (Backend-Neustart, hängender Proxy) endet die Suche
            // nach SUCHE_ZEITLIMIT_MS als Fehler — „Suche …" bleibt nie stehen.
            zeitlimit = setTimeout(() => {
                zeitUeberschritten = true;
                controller.abort();
            }, SUCHE_ZEITLIMIT_MS);
            try {
                const rows = await mitAbbruch(
                    sucheKontakteFuerZuweisung(
                        s2,
                        { typ: props.nurTyp, limit: SEITE + 1, ohneInterne: props.ohneInterne },
                        controller.signal,
                    ),
                    controller.signal,
                );
                setTreffer(ohneGewaehlte(rows.slice(0, SEITE)));
                setWeitere(rows.length > SEITE);
                setAktiv(0);
            } catch (e: any) {
                // Ein Abbruch durch eine neuere Eingabe ist kein Fehler — wohl
                // aber einer durch das Zeitlimit.
                if (!abgeloest) {
                    console.warn("[KontaktSuchfeld] Suche fehlgeschlagen", e);
                    setTreffer([]);
                    setWeitere(false);
                    setFehler(true);
                    setZuLange(zeitUeberschritten);
                }
            } finally {
                clearTimeout(zeitlimit);
                if (!abgeloest) setLaedt(false);
            }
        }, DEBOUNCE_MS);
        return () => {
            abgeloest = true;
            clearTimeout(handle);
            clearTimeout(zeitlimit);
            controller.abort();
            mehrRef.current?.abort();
        };
    }, [term, props.nurTyp, props.ohneInterne, gewaehlt, ohneGewaehlte, versuch]);

    /**
     * Nächste Seite anhängen. Der Versatz ist die aktuelle Trefferzahl — die
     * Server-Sortierung ist über die Id eindeutig, Seiten überlappen also nicht.
     * Der Dedupe-Filter ist trotzdem da: ein zwischenzeitlich angelegter Kontakt
     * kann die Reihenfolge verschieben, und zwei gleiche React-Keys wären ein
     * harter Renderfehler.
     */
    const mehrLaden = React.useCallback(async () => {
        const s2 = term.trim();
        if (s2.length < KONTAKT_SUCHE_MIN_ZEICHEN || laedtMehr) return;
        mehrRef.current?.abort();
        const controller = new AbortController();
        mehrRef.current = controller;
        setLaedtMehr(true);
        try {
            const rows = await sucheKontakteFuerZuweisung(
                s2,
                {
                    typ: props.nurTyp,
                    limit: SEITE + 1,
                    offset: treffer.length,
                    ohneInterne: props.ohneInterne,
                },
                controller.signal,
            );
            if (controller.signal.aborted) return;
            setTreffer((alt) => {
                const bekannt = new Set(alt.map((t) => t.contactId));
                const neu = ohneGewaehlte(rows.slice(0, SEITE)).filter(
                    (t) => !bekannt.has(t.contactId),
                );
                return [...alt, ...neu];
            });
            setWeitere(rows.length > SEITE);
        } catch (e: any) {
            if (e?.name !== "AbortError") {
                console.warn("[KontaktSuchfeld] Nachladen fehlgeschlagen", e);
                setWeitere(false);
            }
        } finally {
            if (!controller.signal.aborted) setLaedtMehr(false);
        }
    }, [term, props.nurTyp, props.ohneInterne, treffer.length, laedtMehr, ohneGewaehlte]);

    const waehle = (t: KontaktTreffer) => {
        props.onChange(t);
        setMausInListe(false);
        setPerTastatur(false);
        setTreffer([]);
        setWeitere(false);
        setTerm("");
    };

    const zuruecksetzen = () => {
        setChipHover(false);
        props.onChange(null);
        setTerm("");
        setTreffer([]);
        setWeitere(false);
        window.setTimeout(() => feldRef.current?.focus(), 0);
    };

    if (gewaehlt) {
        return (
            <div className={f.field}>
                <span className={f.label}>
                    {props.label}
                    {props.required ? <span className={f.req}> *</span> : null}
                </span>
                <div
                    className={s.chip}
                    ref={setChipEl}
                    onMouseEnter={() => setChipHover(true)}
                    onMouseLeave={() => setChipHover(false)}
                    aria-describedby={chipHover ? `${props.name}-karte` : undefined}
                >
                    <TrefferInhalt t={gewaehlt} kisAusgeschrieben={chipBreite >= KIS_AUSGESCHRIEBEN_AB_PX} />
                    <button type="button" className={s.aendern} onClick={zuruecksetzen}>
                        ändern
                    </button>
                </div>
                <VerzoegerteHoverKarte anker={chipEl} offen={chipHover} id={`${props.name}-karte`}>
                    <TrefferDetails t={gewaehlt} />
                </VerzoegerteHoverKarte>
            </div>
        );
    }

    const zuKurz = term.trim().length > 0 && term.trim().length < KONTAKT_SUCHE_MIN_ZEICHEN;
    const offen = treffer.length > 0;
    const listenId = `${props.name}-liste`;
    const karteId = `${props.name}-karte`;
    const karteOffen = offen && (mausInListe || perTastatur);
    const kisAusgeschrieben = listenBreite >= KIS_AUSGESCHRIEBEN_AB_PX;

    const taste = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setPerTastatur(true);
            // Am unteren Ende angekommen, holt die Pfeiltaste die nächste Seite.
            // Ohne das wäre „Mehr laden" für Tastaturbedienung unerreichbar: der
            // Fokus bleibt im Eingabefeld, der Button liegt darunter in der Liste.
            if (aktiv >= treffer.length - 1 && weitere && !laedtMehr) void mehrLaden();
            setAktiv((i) => Math.min(i + 1, treffer.length - 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setPerTastatur(true);
            setAktiv((i) => Math.max(i - 1, 0));
        } else if (e.key === "Enter") {
            if (treffer[aktiv]) {
                e.preventDefault();
                waehle(treffer[aktiv]);
            }
        } else if (e.key === "Escape") {
            setTreffer([]);
        } else {
            // Weitertippen: die Karte der bisher aktiven Zeile gehört weg.
            setPerTastatur(false);
        }
    };

    return (
        <div className={mergeClasses(f.field, s.box)}>
            <label className={f.label} htmlFor={props.name}>
                {props.label}
                {props.required ? <span className={f.req}> *</span> : null}
            </label>
            <div className={f.inputWrap}>
                <input
                    id={props.name}
                    name={props.name}
                    ref={feldRef}
                    className={f.input}
                    type="text"
                    role="combobox"
                    aria-expanded={offen}
                    aria-controls={listenId}
                    aria-autocomplete="list"
                    aria-activedescendant={offen ? `${listenId}-${aktiv}` : undefined}
                    autoComplete="off"
                    autoFocus={props.autoFocus}
                    placeholder={props.placeholder ?? "Name, Firma, Kundennummer oder E-Mail …"}
                    value={term}
                    onChange={(e) => setTerm(e.target.value)}
                    onBlur={() => setBeruehrt(true)}
                    onKeyDown={taste}
                />
            </div>
            {offen && (
                <ul
                    className={s.liste}
                    id={listenId}
                    role="listbox"
                    aria-label={props.label}
                    ref={setListeEl}
                    onMouseEnter={() => setMausInListe(true)}
                    onMouseLeave={() => setMausInListe(false)}
                >
                    {treffer.map((t, i) => (
                        <li key={t.contactId} role="presentation">
                            <button
                                type="button"
                                id={`${listenId}-${i}`}
                                ref={(el) => {
                                    zeilenRefs.current[i] = el;
                                }}
                                role="option"
                                aria-selected={i === aktiv}
                                aria-describedby={i === aktiv && karteOffen ? karteId : undefined}
                                className={mergeClasses(s.zeile, i === aktiv && s.zeileAktiv)}
                                // onMouseDown statt onClick: der Blur des Feldes
                                // darf die Zeile nicht vorher wegnehmen.
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    waehle(t);
                                }}
                                onMouseEnter={() => {
                                    setAktiv(i);
                                    setMausInListe(true);
                                    setPerTastatur(false);
                                }}
                            >
                                <TrefferInhalt t={t} kisAusgeschrieben={kisAusgeschrieben} />
                            </button>
                        </li>
                    ))}
                    {weitere && (
                        <li role="presentation">
                            <button
                                type="button"
                                className={s.mehr}
                                disabled={laedtMehr}
                                // Wie in der Trefferzeile: der Blur des Feldes darf
                                // die Liste nicht wegnehmen, bevor der Klick ankommt.
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    void mehrLaden();
                                }}
                            >
                                {laedtMehr ? "Lädt …" : "Mehr laden"}
                            </button>
                        </li>
                    )}
                </ul>
            )}
            {offen && treffer[aktiv] ? (
                // key = Kontakt: jeder Zeilenwechsel startet die 2 s neu.
                <VerzoegerteHoverKarte
                    key={treffer[aktiv].contactId}
                    anker={zeilenRefs.current[aktiv] ?? null}
                    offen={karteOffen}
                    id={karteId}
                >
                    <TrefferDetails t={treffer[aktiv]} />
                </VerzoegerteHoverKarte>
            ) : null}
            {zuKurz && <span className={f.hintMsg}>Mindestens {KONTAKT_SUCHE_MIN_ZEICHEN} Zeichen.</span>}
            {!zuKurz && laedt && <span className={f.hintMsg}>Suche …</span>}
            {!zuKurz && !laedt && fehler && (
                <span className={f.errorMsg} role="alert">
                    {zuLange ? "Suche dauert zu lange" : "Suche fehlgeschlagen"} —{" "}
                    <button
                        type="button"
                        className={s.erneutLink}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => setVersuch((v) => v + 1)}
                    >
                        erneut versuchen
                    </button>
                </span>
            )}
            {!zuKurz && !laedt && !fehler && beruehrt && term.trim().length >= KONTAKT_SUCHE_MIN_ZEICHEN && treffer.length === 0 && (
                <span className={f.hintMsg}>Keine Treffer. Lege den Kontakt unten neu an.</span>
            )}
            {props.hint && <span className={f.hintMsg}>{props.hint}</span>}
        </div>
    );
}

export default KontaktSuchfeld;
